import { useTranslation } from 'react-i18next';
import { Typography } from '@kizen/kds/Typography';

import Modal from 'components/Modals/presets/BasicModal';
import { Spacer } from '@kizen/kds/Spacer';
import { formatBillingCurrency } from '../../../util';
import { ensureTrailingPeriod } from '../../util';
import type { BillingCadence } from '../PlanSummary';

type ConfirmPlanChangesPreview = {
  monthly_cost_summary?: { total?: string | number };
  upgrade_cost_summary?: { total?: string | number };
};

type ConfirmPlanChangesDialogProps = {
  modalProps: any;
  billingPreviewResponse?: ConfirmPlanChangesPreview | null;
  planChangeClarification?: string;
  cadence: BillingCadence;
};

export const ConfirmPlanChangesDialog = (
  props: ConfirmPlanChangesDialogProps
) => {
  const { t } = useTranslation();

  const { modalProps, billingPreviewResponse, planChangeClarification, cadence } =
    props;

  const hasPreview = Boolean(billingPreviewResponse);
  const displayedTotal = Number(
    billingPreviewResponse?.monthly_cost_summary?.total ?? 0
  );

  const periodAbbreviation = cadence === 'year' ? t('yr') : t('mo');

  const overagesText =
    cadence === 'year'
      ? t(
          'Overages are billed when your balance reaches your billing threshold.'
        )
      : t(
          'Overages are billed monthly, or when your balance reaches your billing threshold.'
        );

  return (
    <Modal
      {...modalProps}
      size="delay_editor"
      heading={t('Confirm Plan Changes')}
      buttonText={t('Confirm')}
    >
      <div className="pb-2">
        <Typography>
          {t(
            'Please confirm the requested changes to your plan and agree to the new charges.'
          )}
        </Typography>
        {hasPreview ? (
          <>
            <Spacer size={20} />
            <Typography>
              <strong>
                {cadence === 'year'
                  ? t('New annual total:')
                  : t('New monthly total:')}
              </strong>{' '}
              {formatBillingCurrency(displayedTotal)}/{periodAbbreviation}
            </Typography>
            <Spacer size={10} />
            <Typography>
              <strong>{t('Amount billed today:')}</strong>{' '}
              {formatBillingCurrency(
                Number(billingPreviewResponse?.upgrade_cost_summary?.total ?? 0)
              )}
            </Typography>
          </>
        ) : null}
        <Spacer size={20} />
        {planChangeClarification ? (
          <>
            <Typography>
              {ensureTrailingPeriod(planChangeClarification)}
            </Typography>
            <Spacer size={10} />
          </>
        ) : null}
        <Typography>{overagesText}</Typography>
      </div>
    </Modal>
  );
};
